import type { LedgerEntryAccountType } from "./ledger-line-item";

export const LEDGER_ACCOUNT_TYPE_VALUES = ["asset", "liability", "equity", "revenue", "expense"] as const;

export type LedgerAccountType = (typeof LEDGER_ACCOUNT_TYPE_VALUES)[number];

export const LEDGER_ACCOUNT_SUBTYPES_BY_TYPE = {
  asset: ["cash", "accounts_receivable", "other_current_asset", "fixed_asset", "other_noncurrent_asset"],
  liability: ["accounts_payable", "other_current_liability", "long_term_liability"],
  equity: ["retained_earnings", "other_equity"],
  revenue: ["operating_revenue", "other_income"],
  expense: [
    "cost_of_revenue",
    "operating_expense",
    "interest_expense",
    "income_tax_expense",
    "other_expense",
  ],
} as const satisfies Record<LedgerAccountType, readonly string[]>;

export const LEDGER_ACCOUNT_SUBTYPE_VALUES = [
  ...LEDGER_ACCOUNT_SUBTYPES_BY_TYPE.asset,
  ...LEDGER_ACCOUNT_SUBTYPES_BY_TYPE.liability,
  ...LEDGER_ACCOUNT_SUBTYPES_BY_TYPE.equity,
  ...LEDGER_ACCOUNT_SUBTYPES_BY_TYPE.revenue,
  ...LEDGER_ACCOUNT_SUBTYPES_BY_TYPE.expense,
] as const;

export type LedgerAccountSubtype = (typeof LEDGER_ACCOUNT_SUBTYPE_VALUES)[number];

export const LEDGER_ACCOUNT_TYPE_BY_SUBTYPE = {
  cash: "asset",
  accounts_receivable: "asset",
  other_current_asset: "asset",
  fixed_asset: "asset",
  other_noncurrent_asset: "asset",
  accounts_payable: "liability",
  other_current_liability: "liability",
  long_term_liability: "liability",
  retained_earnings: "equity",
  other_equity: "equity",
  operating_revenue: "revenue",
  other_income: "revenue",
  cost_of_revenue: "expense",
  operating_expense: "expense",
  interest_expense: "expense",
  income_tax_expense: "expense",
  other_expense: "expense",
} as const satisfies Record<LedgerAccountSubtype, LedgerAccountType>;

export const LEDGER_ACCOUNT_STATEMENT_SECTION_VALUES = [
  "current_asset",
  "noncurrent_asset",
  "current_liability",
  "noncurrent_liability",
  "equity",
  "operating_revenue",
  "nonoperating_income",
  "cost_of_revenue",
  "operating_expense",
  "nonoperating_expense",
] as const;

export type LedgerAccountStatementSection = (typeof LEDGER_ACCOUNT_STATEMENT_SECTION_VALUES)[number];

export const LEDGER_ACCOUNT_STATEMENT_SECTION_BY_SUBTYPE = {
  cash: "current_asset",
  accounts_receivable: "current_asset",
  other_current_asset: "current_asset",
  fixed_asset: "noncurrent_asset",
  other_noncurrent_asset: "noncurrent_asset",
  accounts_payable: "current_liability",
  other_current_liability: "current_liability",
  long_term_liability: "noncurrent_liability",
  retained_earnings: "equity",
  other_equity: "equity",
  operating_revenue: "operating_revenue",
  other_income: "nonoperating_income",
  cost_of_revenue: "cost_of_revenue",
  operating_expense: "operating_expense",
  interest_expense: "nonoperating_expense",
  income_tax_expense: "nonoperating_expense",
  other_expense: "nonoperating_expense",
} as const satisfies Record<LedgerAccountSubtype, LedgerAccountStatementSection>;

export const LEDGER_ACCOUNT_NORMAL_BALANCE_VALUES = ["debit", "credit"] as const;

export type LedgerAccountNormalBalance = (typeof LEDGER_ACCOUNT_NORMAL_BALANCE_VALUES)[number];

export const LEDGER_ACCOUNT_NORMAL_BALANCE_BY_SUBTYPE = {
  cash: "debit",
  accounts_receivable: "debit",
  other_current_asset: "debit",
  fixed_asset: "debit",
  other_noncurrent_asset: "debit",
  accounts_payable: "credit",
  other_current_liability: "credit",
  long_term_liability: "credit",
  retained_earnings: "credit",
  other_equity: "credit",
  operating_revenue: "credit",
  other_income: "credit",
  cost_of_revenue: "debit",
  operating_expense: "debit",
  interest_expense: "debit",
  income_tax_expense: "debit",
  other_expense: "debit",
} as const satisfies Record<LedgerAccountSubtype, LedgerAccountNormalBalance>;

export const LEDGER_ACCOUNT_CLOSING_BEHAVIOR_VALUES = ["permanent", "close_to_retained_earnings"] as const;

export type LedgerAccountClosingBehavior = (typeof LEDGER_ACCOUNT_CLOSING_BEHAVIOR_VALUES)[number];

export const LEDGER_ACCOUNT_CLOSING_BEHAVIOR_BY_SUBTYPE = {
  cash: "permanent",
  accounts_receivable: "permanent",
  other_current_asset: "permanent",
  fixed_asset: "permanent",
  other_noncurrent_asset: "permanent",
  accounts_payable: "permanent",
  other_current_liability: "permanent",
  long_term_liability: "permanent",
  retained_earnings: "permanent",
  other_equity: "permanent",
  operating_revenue: "close_to_retained_earnings",
  other_income: "close_to_retained_earnings",
  cost_of_revenue: "close_to_retained_earnings",
  operating_expense: "close_to_retained_earnings",
  interest_expense: "close_to_retained_earnings",
  income_tax_expense: "close_to_retained_earnings",
  other_expense: "close_to_retained_earnings",
} as const satisfies Record<LedgerAccountSubtype, LedgerAccountClosingBehavior>;

export const LEDGER_ENTRY_ACCOUNT_TYPE_BY_LEDGER_ACCOUNT_TYPE = {
  asset: "asset",
  liability: "liability",
  equity: "equity",
  revenue: "income",
  expense: "expense",
} as const satisfies Record<LedgerAccountType, LedgerEntryAccountType>;

export type LedgerAccountReportingProfile = {
  type: LedgerAccountType;
  statementSection: LedgerAccountStatementSection;
  normalBalance: LedgerAccountNormalBalance;
  closingBehavior: LedgerAccountClosingBehavior;
  ledgerEntryAccountType: LedgerEntryAccountType;
};
